import React from 'react';
import useSeoTags from '../utils/useSeoTags';
import FdFamiliesTicker from './FdFamiliesTicker';
import FdReviewsMarquee from './FdReviewsMarquee';
import FdWorkflowPreview from './FdWorkflowPreview';
import FdRoiCalc from './FdRoiCalc';
import FdPricingTiles from './FdPricingTiles';
import FdExitIntent from './FdExitIntent';

/**
 * B2B marketing / program page for funeral homes & cremation services.
 *
 * Lives in its own visual world (glass cards, pastel orbs, quill/lily
 * watermarks, full-bleed side-card artwork) so funeral professionals
 * immediately understand this is a commercial tool, not the consumer
 * obituary generator. The markup is wrapped in `.fd-auth .fd-landing`
 * so it inherits the portal design tokens + button styles defined
 * alongside the dashboard.
 *
 * Page sections, in order:
 *   1.  Social-proof ticker
 *   2.  Hero (value prop) + animated workflow preview
 *   3.  "What we don't do" reassurance strip
 *   4.  Trust badges triple
 *   5.  Live testimonials marquee
 *   6.  Pricing tiles
 *   7.  ROI calculator (interactive chart)
 *   8.  vs. writing by hand comparison table
 *   9.  FAQ (visible + JSON-LD mirror)
 *  10.  Exit-intent modal (fires on cursor-leave)
 */
const FuneralDirectorsLanding = () => {
  const CANONICAL = 'https://obitauthor.com/funeral-directors';
  const OG_IMAGE = 'https://obitauthor.com/fd-portal-glass.png';
  const SAMPLE_PDF_URL = '/sample-branded-obituary.pdf';

  const faqs = [
    {
      q: 'What is ObitAuthor for Funeral Professionals?',
      a: 'ObitAuthor for Funeral Professionals is a credit-based web app that helps funeral homes, cremation services, and independent funeral directors draft, edit, and deliver professional obituaries in minutes. Each obituary is downloadable as a branded PDF carrying your firm\u2019s name and logo \u2014 never ObitAuthor branding \u2014 and is saved to a searchable library under your account.',
    },
    {
      q: 'How much does the funeral-home obituary software cost?',
      a: 'New firms get 30 credits free when they create an account, with no billing information required. After the trial, credits are sold in prepaid bundles of 50, 100, 500, 1,000, and 5,000. The effective per-obituary cost runs 75\u00a2 to $1, and credits never expire while your account is in good standing.',
    },
    {
      q: 'Is this a white-label obituary service?',
      a: 'Yes. Every obituary PDF generated through the Funeral Professional portal is rendered with your firm\u2019s name and uploaded logo in the document header, not ObitAuthor\u2019s. Families you serve see only your branding on the final printed tribute.',
    },
    {
      q: 'How does the free trial work for funeral directors?',
      a: 'Signing up creates a free Funeral Professional account preloaded with 30 obituary credits. You do not need a credit card to start \u2014 just your firm email and a password. One credit produces one full AI-generated obituary draft; edits, redownloads, and manually-written obituaries never consume credits.',
    },
    {
      q: 'Can my staff edit the AI-generated obituary before it is delivered to the family?',
      a: 'Absolutely. Every generated draft opens in an in-browser rich-text editor where staff can adjust phrasing, add or remove survivors, fix dates, and personalize tone. Saved obituaries can be reopened and re-edited from your firm\u2019s library at any time.',
    },
    {
      q: 'How much time does an AI obituary generator save a funeral home?',
      a: 'Directors typically spend 30\u201360 minutes drafting an obituary from intake notes. ObitAuthor produces a respectful, complete first draft in well under a minute, so staff time is spent reviewing and personalizing rather than writing from scratch \u2014 roughly 30 minutes saved per family.',
    },
    {
      q: 'Where are obituaries stored, and who can see them?',
      a: 'Saved obituaries live under your firm\u2019s account and are visible only to authenticated staff you add. ObitAuthor does not publish decedent details, does not share obituary content with third parties, and does not use firm-side inputs to train outside models. See the Funeral Professional Privacy Policy for details.',
    },
    {
      q: 'How is ObitAuthor different from writing obituaries in Word?',
      a: 'Word is a blank page; ObitAuthor is a workflow. You paste rough intake notes, a respectful first draft appears, and every draft automatically applies your firm\u2019s branded PDF template. Saved obituaries are searchable under the firm\u2019s account so re-downloads six months later take seconds, not a disk search.',
    },
  ];

  useSeoTags({
    title:
      'Obituary Software for Funeral Homes — White-Label AI Obituary Generator | ObitAuthor',
    description:
      'Obituary writing software for funeral directors and cremation services. Draft branded obituaries in seconds, save 30 minutes per family, and start with 30 free credits — no credit card required.',
    keywords:
      'obituary software for funeral homes, funeral director obituary generator, AI obituary generator, white label obituary service, funeral home software, obituary writing tool, cremation service software, ObitAuthor for funeral professionals',
    canonical: CANONICAL,
    ogImage: OG_IMAGE,
    ogType: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'ObitAuthor for Funeral Professionals',
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'Funeral Home Software',
        operatingSystem: 'Web',
        url: CANONICAL,
        image: OG_IMAGE,
        description:
          'White-label AI obituary generator for funeral homes and cremation services. Credit-based pricing, branded PDFs, and a saved obituary library for every family your firm serves.',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: '30 obituary credits free on signup. No credit card required.',
          url: 'https://obitauthor.com/funeral-directors/dashboard?mode=register',
          availability: 'https://schema.org/InStock',
        },
        featureList: [
          'White-label PDF branding with firm name and logo',
          'Credit-based generations (1 credit per AI obituary)',
          '30 free trial credits on signup',
          'Rich-text editor for in-browser adjustments',
          'Saved obituary library under the firm account',
          'Bulk credit bundles: 50, 100, 500, 1,000, 5,000',
        ],
        provider: {
          '@type': 'Organization',
          name: 'ObitAuthor',
          url: 'https://obitauthor.com/',
          logo: 'https://obitauthor.com/logo.png',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ObitAuthor', item: 'https://obitauthor.com/' },
          { '@type': 'ListItem', position: 2, name: 'For Funeral Professionals', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  });

  const Check = () => (
    <span className="fd-landing-bullet" aria-hidden="true">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );

  return (
    <main className="container py-5 fd-auth fd-landing funeral-directors-landing">
      {/* B2B social-proof ticker: real /api/fd/public-stats count with
          a sensible floor so this reads as legitimate on day one. */}
      <div className="d-flex justify-content-center mb-4">
        <FdFamiliesTicker />
      </div>

      {/* ==========================================================
          HERO
          Left: sharpened value prop + benefits + dual CTAs.
          Right: animated workflow preview (replaces static art).
          ========================================================== */}
      <div className="row justify-content-center g-4 align-items-stretch">
        <div className="col-lg-7 d-flex">
          <article className="glass-card fd-landing-card flex-fill">
            <span className="fd-auth-badge">For funeral professionals</span>

            <h1 className="fd-landing-title mt-3 mb-3">
              Give your directors 30 minutes back on every obituary
              <br className="d-none d-md-inline" />
              {' '}— without losing your firm&apos;s voice.
            </h1>

            <p className="fd-landing-sub mb-4">
              Stop copy-pasting from old Word templates or paying a writer
              $75 per obit. ObitAuthor drafts respectful, print-ready
              obituaries on your firm&apos;s branded PDF template,
              saves them under your account, and gets out of your way.
            </p>

            <div className="fd-landing-highlight mb-4" role="note">
              <div className="fd-landing-highlight-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <div>
                <strong className="d-block">30 credits free when you sign up.</strong>
                <span className="fd-landing-highlight-sub">
                  No card required. Polished obituaries from{' '}
                  <strong>75&cent; to $1 each </strong> at volume.
                </span>
              </div>
            </div>

            <h2 className="visually-hidden">What funeral directors get</h2>
            <ul className="fd-landing-benefits">
              <li><Check />
                <span><strong>Your company name &amp; logo</strong> on every saved PDF — no ObitAuthor branding in the header.</span>
              </li>
              <li><Check />
                <span>Credit-based generations — 1 credit per AI obituary. Credits never expire.</span>
              </li>
              <li><Check />
                <span>Volume packages: 50, 100, 500, 1,000, or 5,000 credits.</span>
              </li>
              <li><Check />
                <span>Full <strong>library of obituaries</strong> saved under your firm, searchable and re-downloadable anytime.</span>
              </li>
              <li><Check />
                <span>Rich-text editor for quick in-browser tweaks before print.</span>
              </li>
            </ul>

            <div className="d-flex flex-wrap gap-3 mt-4">
              <a
                href="/funeral-directors/dashboard?mode=register"
                className="btn btn-primary btn-lg"
                aria-label="Try it free — 30 obituaries, no credit card"
              >
                Try it free — no card, 30 obituaries
              </a>
              <a
                href={SAMPLE_PDF_URL}
                className="btn btn-outline-primary btn-lg"
                download="ObitAuthor-sample-obituary.pdf"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
                     strokeLinejoin="round" style={{ marginRight: 6 }} aria-hidden="true">
                  <path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M4 21h16" />
                </svg>
                See a sample PDF
              </a>
            </div>

            <p className="fd-landing-privacy-line mt-4 mb-0">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                   strokeLinejoin="round" aria-hidden="true">
                <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              <span>
                Family data stays private. We never publish decedent details,
                never share obituary content, and never use firm-side inputs
                to train outside models.{' '}
                <a href="/funeral-directors/privacy">Read our policy</a>.
              </span>
            </p>
          </article>
        </div>

        {/* RIGHT: animated workflow preview + highlights/stats/steps */}
        <div className="col-lg-5 d-flex">
          <aside className="glass-card fd-landing-side fd-landing-side-has-art flex-fill">
            <FdWorkflowPreview />

            <div className="fd-landing-stats" role="list" aria-label="Program highlights">
              <div className="fd-landing-stat" role="listitem">
                <div className="fd-landing-stat-value">30</div>
                <div className="fd-landing-stat-label">Free credits on signup</div>
              </div>
              <div className="fd-landing-stat" role="listitem">
                <div className="fd-landing-stat-value">~30 min</div>
                <div className="fd-landing-stat-label">Saved per family</div>
              </div>
              <div className="fd-landing-stat" role="listitem">
                <div className="fd-landing-stat-value">75&cent;–$1</div>
                <div className="fd-landing-stat-label">Per obituary at volume</div>
              </div>
            </div>

            <div className="fd-landing-side-divider" aria-hidden="true" />

            <h2 className="fd-landing-side-title">How funeral directors use it</h2>
            <ol className="fd-landing-steps">
              <li>
                <span className="fd-landing-step-num">1</span>
                <span>
                  Create a free account with your firm name and logo — we
                  apply them to every PDF automatically.
                </span>
              </li>
              <li>
                <span className="fd-landing-step-num">2</span>
                <span>
                  Enter a few details or paste rough intake notes. Our AI
                  drafts a respectful, complete obituary in seconds.
                </span>
              </li>
              <li>
                <span className="fd-landing-step-num">3</span>
                <span>
                  Review and tweak in the browser, then download a
                  print-ready branded PDF for the family.
                </span>
              </li>
            </ol>

            <div className="fd-landing-side-foot">
              <span>Ready to try it?</span>{' '}
              <a href="/funeral-directors/dashboard?mode=register">
                Start the free trial
              </a>{' '}
              and run a generation — it takes under a minute.
            </div>
          </aside>
        </div>
      </div>

      {/* ==========================================================
          "WHAT WE DON'T DO" reassurance strip — calms the most
          common director objections (family email, publishing,
          CRM scraping) without needing a sales call.
          ========================================================== */}
      <section className="fd-landing-promises mt-5" aria-labelledby="fd-promises-heading">
        <div className="glass-card fd-landing-promises-card">
          <span className="fd-auth-badge">What we don&apos;t do</span>
          <h2 id="fd-promises-heading" className="fd-landing-promises-title mt-3 mb-3">
            The things we deliberately never touch
          </h2>
          <ul className="fd-landing-promises-list">
            <li>
              <span className="fd-landing-promise-icon" aria-hidden="true">×</span>
              <div>
                <strong>We never email the family.</strong>
                <span>
                  Delivery is your firm&apos;s relationship — we hand you a PDF
                  and a saved record, not a mass mailing tool.
                </span>
              </div>
            </li>
            <li>
              <span className="fd-landing-promise-icon" aria-hidden="true">×</span>
              <div>
                <strong>We never publish obituaries publicly.</strong>
                <span>
                  Every draft lives in your private firm library. If a family
                  wants it published, that&apos;s you posting it on your site.
                </span>
              </div>
            </li>
            <li>
              <span className="fd-landing-promise-icon" aria-hidden="true">×</span>
              <div>
                <strong>We never touch your CRM.</strong>
                <span>
                  No integrations, no scraping, no contact imports. ObitAuthor
                  is a writing tool, not a marketing platform.
                </span>
              </div>
            </li>
            <li>
              <span className="fd-landing-promise-icon" aria-hidden="true">×</span>
              <div>
                <strong>We never train outside models on your inputs.</strong>
                <span>
                  Decedent details and obituary text you put into the tool
                  are not used to train any third-party model.
                </span>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* ==========================================================
          TRUST BADGES STRIP — cloned from the auth page so the
          signup objection defuses hit before the reviews. Pure
          decoration; copy reinforces brand, safety, and trial terms.
          ========================================================== */}
      <div className="fd-auth-badges mt-5" role="list">
        <div className="fd-auth-badge-card" role="listitem">
          <span className="fd-auth-badge-icon fd-auth-badge-icon-brand" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="13" rx="2" />
              <path d="M7 20h10" /><path d="M12 17v3" />
              <path d="M7 9h6" /><path d="M7 12h10" />
            </svg>
          </span>
          <div className="fd-auth-badge-text">
            <strong>White-label branding</strong>
            <span>Your firm&apos;s name &amp; logo on every PDF</span>
          </div>
        </div>
        <div className="fd-auth-badge-card" role="listitem">
          <span className="fd-auth-badge-icon fd-auth-badge-icon-safe" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </span>
          <div className="fd-auth-badge-text">
            <strong>No billing info required</strong>
            <span>Start without a credit card on file</span>
          </div>
        </div>
        <div className="fd-auth-badge-card" role="listitem">
          <span className="fd-auth-badge-icon fd-auth-badge-icon-trial" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l2.39 4.84L20 8l-4 3.9.94 5.5L12 14.77 7.06 17.4 8 11.9 4 8l5.61-1.16L12 2z" />
            </svg>
          </span>
          <div className="fd-auth-badge-text">
            <strong>30-obituary free trial</strong>
            <span>Credits never expire while you test fit</span>
          </div>
        </div>
      </div>

      {/* ==========================================================
          Testimonials marquee — merges director-submitted approved
          reviews (from /api/fd/public-reviews) with a seeded deck.
          ========================================================== */}
      <FdReviewsMarquee />

      {/* ==========================================================
          PRICING TILES — concrete numbers before the FAQ. Directors
          buying software want to see a price sticker before they'll
          hand over an email.
          ========================================================== */}
      <section className="mt-5">
        <FdPricingTiles />
      </section>

      {/* ==========================================================
          ROI CALCULATOR — interactive pastel SVG chart. Reframes
          "this costs money" as "this saves money".
          ========================================================== */}
      <section className="mt-5">
        <FdRoiCalc />
      </section>

      {/* ==========================================================
          VS. WRITING BY HAND comparison — answers the commercial
          intent query ("obituary software vs writing by hand") with
          keyword-rich copy Google surfaces for direct-response SEO.
          ========================================================== */}
      <section className="mt-5" aria-labelledby="fd-compare-heading">
        <div className="glass-card fd-compare-card">
          <span className="fd-auth-badge">vs. Writing by hand</span>
          <h2 id="fd-compare-heading" className="fd-compare-title mt-3">
            ObitAuthor vs. drafting obituaries in Word or a notepad
          </h2>
          <p className="fd-compare-sub">
            The honest side-by-side, from funeral directors who&apos;ve
            tried both. Your mileage will vary with firm size and the
            kinds of services you run.
          </p>
          <div className="fd-compare-table-wrap" role="region" aria-label="Obituary workflow comparison">
            <table className="fd-compare-table">
              <thead>
                <tr>
                  <th scope="col">Dimension</th>
                  <th scope="col">Writing by hand</th>
                  <th scope="col" className="fd-compare-col-us">With ObitAuthor</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Time per obituary</th>
                  <td>30&ndash;60 minutes typing from intake notes</td>
                  <td className="fd-compare-col-us">Under 5 minutes reviewing &amp; tweaking</td>
                </tr>
                <tr>
                  <th scope="row">Branding</th>
                  <td>Manual Word header each time — easy to forget</td>
                  <td className="fd-compare-col-us">Firm name &amp; logo applied automatically</td>
                </tr>
                <tr>
                  <th scope="row">Cost</th>
                  <td>Staff hours (often $25&ndash;$50/hr loaded)</td>
                  <td className="fd-compare-col-us">75&cent; to $1 per generation at volume</td>
                </tr>
                <tr>
                  <th scope="row">Tone consistency</th>
                  <td>Varies wildly by director / day</td>
                  <td className="fd-compare-col-us">Respectful, complete first draft every time</td>
                </tr>
                <tr>
                  <th scope="row">Retrieval six months later</th>
                  <td>Search your computer / shared drive</td>
                  <td className="fd-compare-col-us">Searchable firm library, re-download in 1 click</td>
                </tr>
                <tr>
                  <th scope="row">Print-ready PDF</th>
                  <td>Word &rarr; PDF &rarr; fix margins &rarr; print</td>
                  <td className="fd-compare-col-us">Generated, branded, letter-size, right away</td>
                </tr>
                <tr>
                  <th scope="row">Credit-card commitment</th>
                  <td>N/A (you&apos;re already paying staff)</td>
                  <td className="fd-compare-col-us">No card to sign up. 30 free credits to try it.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="fd-compare-foot">
            <a
              href="/funeral-directors/dashboard?mode=register"
              className="btn btn-primary"
            >
              Try it free on your next obituary
            </a>
          </div>
        </div>
      </section>

      {/* ==========================================================
          FAQ — visible section mirrors the FAQPage JSON-LD above.
          ========================================================== */}
      <section className="row g-4 mt-4" aria-labelledby="fd-faq-heading" id="fd-faq">
        <div className="col-12">
          <div className="glass-card fd-landing-faq">
            <span className="fd-auth-badge">FAQ</span>
            <h2 id="fd-faq-heading" className="fd-landing-faq-title mt-3">
              Funeral-director questions, answered
            </h2>
            <p className="fd-landing-faq-sub">
              Common questions about using ObitAuthor as the obituary
              workflow inside a funeral home or cremation service.
            </p>
            <div className="fd-landing-faq-list">
              {faqs.map((item, i) => (
                <details key={i} className="fd-landing-faq-item">
                  <summary className="fd-landing-faq-q">{item.q}</summary>
                  <p className="fd-landing-faq-a">{item.a}</p>
                </details>
              ))}
            </div>
            <div className="fd-landing-faq-foot">
              Not seeing your question?{' '}
              <a href="/funeral-directors/dashboard?mode=register">
                Start the free trial
              </a>{' '}
              and reach out from your firm dashboard.
            </div>
          </div>
        </div>
      </section>

      {/* Exit-intent modal — offers the sample branded PDF to any
          visitor whose cursor leaves the top of the viewport after
          a short arming delay. Dismissed permanently for the session. */}
      <FdExitIntent sampleHref={SAMPLE_PDF_URL} />
    </main>
  );
};

export default FuneralDirectorsLanding;
